<template>
  <div class="email-viewer">
    <div v-if="email" class="email-viewer__content">
      <div class="email-viewer__header">
        <div class="email-viewer__header-main">
          <div class="email-viewer__subject-row">
            <slot name="back-button" />
            <h2 class="email-viewer__subject">{{ latestEmail.subject || "(No Subject)" }}</h2>
          </div>
          <div class="email-viewer__meta">
            <div class="email-viewer__sender">
              <span class="email-viewer__sender-label">From:</span>
              <span class="email-viewer__sender-value">{{
                getSenderDisplay(latestEmail.from_attendee || latestEmail.from)
              }}</span>
            </div>
            <div class="email-viewer__recipients">
              <span class="email-viewer__recipient-label">To:</span>
              <span class="email-viewer__recipient-value">{{
                getRecipientsDisplay(latestEmail.to_attendees || latestEmail.to)
              }}</span>
            </div>
            <div
              v-if="
                (latestEmail.cc_attendees && latestEmail.cc_attendees.length > 0) ||
                (latestEmail.cc && getRecipientsArray(latestEmail.cc).length > 0)
              "
              class="email-viewer__recipients"
            >
              <span class="email-viewer__recipient-label">CC:</span>
              <span class="email-viewer__recipient-value">{{
                getRecipientsDisplay(latestEmail.cc_attendees || latestEmail.cc)
              }}</span>
            </div>
            <div
              v-if="
                (latestEmail.bcc_attendees && latestEmail.bcc_attendees.length > 0) ||
                (latestEmail.bcc && getRecipientsArray(latestEmail.bcc).length > 0)
              "
              class="email-viewer__recipients"
            >
              <span class="email-viewer__recipient-label">BCC:</span>
              <span class="email-viewer__recipient-value">{{
                getRecipientsDisplay(latestEmail.bcc_attendees || latestEmail.bcc)
              }}</span>
            </div>
            <div class="email-viewer__date">
              <div class="email-viewer__date-left">
                <Icon icon="mdi:clock-outline" />
                <span>{{ formatDate(latestEmail.date) }}</span>
              </div>
              <div class="email-viewer__date-right">
                <div class="email-viewer__read-status-container">
                  <button
                    v-if="latestEmail"
                    class="email-viewer__read-status"
                    :class="{
                      'email-viewer__read-status--read': hasReadReceipts,
                      'email-viewer__read-status--animating': isReadStatusAnimating,
                    }"
                    @click="openReadReceipts($event)"
                    :title="
                      hasReadReceipts
                        ? `View read receipts (${readCount || sortedReadEvents.length})`
                        : 'No read receipts'
                    "
                    :disabled="isLoadingReadReceipts || isLoadingTrackingEvents"
                    :style="{
                      opacity: isLoadingReadReceipts || isLoadingTrackingEvents ? 0.5 : 1,
                    }"
                  >
                    <Icon icon="mdi:check-all" class="email-viewer__read-icon" />
                    <span v-if="readCount > 1" class="email-viewer__read-count-badge">
                      {{ readCount }}
                    </span>
                  </button>
                  <div
                    v-if="isLoadingReadReceipts || isLoadingTrackingEvents"
                    class="email-viewer__loading-indicator"
                  >
                    <LoadingBars size="xs" :show-text="false" />
                  </div>
                </div>
                <VTooltip>
                  <button class="email-viewer__icon-btn" @click="handleDelete">
                    <Icon icon="mdi:delete-outline" width="18" height="18" style="color: #dc2626" />
                  </button>
                  <template #popper> Delete </template>
                </VTooltip>
                <VTooltip v-if="latestEmail?.read_date">
                  <button class="email-viewer__icon-btn" @click="handleMarkAsRead">
                    <Icon
                      icon="material-symbols:mark-email-unread-outline"
                      width="18"
                      height="18"
                      style="color: #6b7280"
                    />
                  </button>
                  <template #popper> Mark as Unread </template>
                </VTooltip>
                <VTooltip v-else>
                  <button class="email-viewer__icon-btn" @click="handleMarkAsRead">
                    <Icon
                      icon="material-symbols:mark-email-read-outline"
                      width="18"
                      height="18"
                      style="color: #6b7280"
                    />
                  </button>
                  <template #popper> Mark as Read </template>
                </VTooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="email-viewer__scrollable-content">
        <div v-if="isLoading" class="email-viewer__body-loading">
          <LoadingBars size="sm" />
        </div>
        <div
          v-else-if="email.is_thread && email.thread_emails && email.thread_emails.length > 1"
          class="email-viewer__gmail-thread"
        >
          <div
            v-for="(threadEmail, index) in sortedThreadEmails"
            :key="threadEmail.id"
            class="email-viewer__gmail-message"
            :class="{
              'email-viewer__gmail-message--unread': !threadEmail.read_date,
            }"
          >
            <div class="email-viewer__gmail-message-header">
              <div class="email-viewer__gmail-message-sender">
                {{ getThreadReplySender(threadEmail) }}
              </div>
              <div class="email-viewer__gmail-message-date">
                {{ formatThreadDate(threadEmail.date) }}
              </div>
            </div>
            <div class="email-viewer__gmail-message-body">
              <!-- Outbound emails: sandboxed iframe to prevent tracking pixel self-opens -->
              <iframe
                v-if="isOutboundThreadEmail(threadEmail) && threadEmail.html_body"
                :srcdoc="sanitizeOutboundHtml(threadEmail.html_body)"
                sandbox=""
                class="email-viewer__html-body-iframe email-viewer__gmail-body"
              ></iframe>
              <div
                v-else-if="threadEmail.html_body"
                class="email-viewer__html-body email-viewer__gmail-body"
                v-html="threadEmail.html_body"
              ></div>
              <div
                v-else-if="threadEmail.text_body"
                class="email-viewer__text-body email-viewer__gmail-body"
              >
                <pre>{{ threadEmail.text_body }}</pre>
              </div>
              <!-- Outbound fallback body -->
              <iframe
                v-else-if="isOutboundThreadEmail(threadEmail) && threadEmail.body"
                :srcdoc="sanitizeOutboundHtml(threadEmail.body)"
                sandbox=""
                class="email-viewer__html-body-iframe email-viewer__gmail-body"
              ></iframe>
              <div
                v-else-if="threadEmail.body"
                class="email-viewer__html-body email-viewer__gmail-body"
                v-html="threadEmail.body"
              ></div>
              <div v-else class="email-viewer__empty-body">
                <p>No content available</p>
              </div>
            </div>
            <div
              v-if="threadEmail.attachments && threadEmail.attachments.length > 0"
              class="email-viewer__gmail-attachments"
            >
              <Icon icon="mdi:paperclip" />
              <span>{{ threadEmail.attachments.length }} attachment(s)</span>
            </div>
          </div>
        </div>
        <div v-else-if="!isLoading" class="email-viewer__body">
          <!-- Outbound emails: sandboxed iframe to prevent tracking pixel self-opens -->
          <iframe
            v-if="isOutboundEmail && latestEmail.html_body"
            :srcdoc="sanitizeOutboundHtml(latestEmail.html_body)"
            sandbox=""
            class="email-viewer__html-body-iframe"
          ></iframe>
          <div
            v-else-if="latestEmail.html_body"
            class="email-viewer__html-body"
            v-html="latestEmail.html_body"
          ></div>
          <div v-else-if="latestEmail.text_body" class="email-viewer__text-body">
            <pre>{{ latestEmail.text_body }}</pre>
          </div>
          <iframe
            v-else-if="isOutboundEmail && latestEmail.body"
            :srcdoc="sanitizeOutboundHtml(latestEmail.body)"
            sandbox=""
            class="email-viewer__html-body-iframe"
          ></iframe>
          <div
            v-else-if="latestEmail.body"
            class="email-viewer__html-body"
            v-html="latestEmail.body"
          ></div>
          <div v-else class="email-viewer__empty-body">
            <p>No content available</p>
          </div>
        </div>
        <div
          v-if="latestEmail.attachments && latestEmail.attachments.length > 0"
          class="email-viewer__attachments email-viewer__attachments--compact"
        >
          <div class="email-viewer__attachments-header">
            <Icon icon="mdi:paperclip" />
            <span>{{ latestEmail.attachments.length }} Attachment(s)</span>
          </div>
          <div class="email-viewer__attachments-list">
            <div
              v-for="attachment in latestEmail.attachments"
              :key="attachment.id"
              class="email-viewer__attachment-item"
              @click="handleDownloadAttachment(attachment)"
            >
              <Icon icon="mdi:file-outline" />
              <span class="email-viewer__attachment-name">{{ attachment.name }}</span>
              <span v-if="attachment.size" class="email-viewer__attachment-size">
                {{ formatFileSize(attachment.size) }}
              </span>
              <Icon icon="mdi:download" class="email-viewer__attachment-download" />
            </div>
          </div>
        </div>
      </div>
      <IcpPicker
        :show="showIcpPicker"
        :mode="icpPickerMode"
        :icps="icps"
        class="email-viewer__icp-picker-positioned"
        @select="handleIcpSelect"
        @dismiss="dismissIcpPicker"
      />
      <div class="email-viewer__footer-actions">
        <Button
          size="sm"
          variant="ghost"
          rounded
          icon="mdi:reply"
          @click="handleReply"
          title="Reply"
        >
          Reply
        </Button>
        <Button
          size="sm"
          variant="ghost"
          rounded
          icon="mdi:forward"
          @click="handleForward"
          title="Forward"
        >
          Forward
        </Button>
        <Button
          size="sm"
          variant="ghost"
          rounded
          icon="mdi:sparkles"
          :disabled="isGeneratingAI"
          @click="handleAiReply"
          title="AI Reply"
        >
          <template v-if="generatingAction === 'reply'">
            <span class="email-viewer__ai-spinner" />
            Generating...
          </template>
          <template v-else>AI Reply</template>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          rounded
          icon="mdi:sparkles"
          :disabled="isGeneratingAI"
          @click="handleAiFollowup"
          title="AI Follow-up"
        >
          <template v-if="generatingAction === 'followup'">
            <span class="email-viewer__ai-spinner" />
            Generating...
          </template>
          <template v-else>AI Follow-up</template>
        </Button>
      </div>
    </div>

    <div v-else-if="isLoading" class="email-viewer__loading">
      <LoadingBars size="sm" />
    </div>

    <div v-else class="email-viewer__empty">
      <div class="email-viewer__empty-content">
        <Icon icon="mdi:email-outline" class="email-viewer__empty-icon" />
        <h3 class="email-viewer__empty-title">Select an email to view</h3>
        <p class="email-viewer__empty-message">
          Choose an email from the list on the left to see its full content, attachments, and
          details here.
        </p>
      </div>
    </div>
    <ReadReceiptsPopover
      :show="showReadReceiptsPopover"
      :read-receipts="formattedReadReceipts"
      :read-count="readCount"
      :trigger-element="readReceiptsTrigger"
      :is-loading="isLoadingReadReceipts"
      @close="closeReadReceipts"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import Button from "@/components/ui/Button.vue";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import IcpPicker from "@/components/ui/IcpPicker.vue";
import { useIcpPicker } from "@/composables/ui/useIcpPicker";
import ReadReceiptsPopover from "../bucket/email/ReadReceiptsPopover.vue";
import Contact from "@/services/contact/contact";
import Unipile from "@/services/unipile/unipile";
import { useEmailWebhooks } from "@/composables/emails/useEmailWebhooks";
import { getSocket, isSocketConnected } from "@/utils/socket";

// Props
const props = defineProps({
  email: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  icps: {
    type: Array,
    default: () => [],
  },
  isGeneratingAI: {
    type: Boolean,
    default: false,
  },
  generatingAction: {
    type: String,
    default: null,
  },
});

// Emits
const emit = defineEmits([
  "reply",
  "forward",
  "delete",
  "mark-as-read",
  "download-attachment",
  "ai-reply",
  "ai-followup",
]);

// Store
const store = useStore();
const account = computed(() => store.getters.account?.data);
const accountId = computed(() => account.value?.id);

// ICP picker
const {
  showIcpPicker,
  icpPickerMode,
  triggerAiAction,
  selectIcp: handleIcpSelect,
  dismissIcpPicker,
} = useIcpPicker((mode, icpId) => {
  if (mode === "reply") emit("ai-reply", { email: props.email, icpId });
  else emit("ai-followup", { email: props.email, icpId });
});

// Reactive state
const showReadReceiptsPopover = ref(false);
const readReceiptsTrigger = ref(null);
const emailTrackingEvents = ref([]);
const readReceipts = ref([]);
const readCount = ref(0);
const isReadStatusAnimating = ref(false);
const isLoadingTrackingEvents = ref(false);
const isLoadingReadReceipts = ref(false);

// Retry configuration for error handling
const MAX_RETRIES = 3;
const RETRY_DELAY = 5000; // 5 seconds
let retryCount = 0;

// Computed properties
const latestEmail = computed(() => {
  if (!props.email || !props.email.is_thread) {
    return props.email;
  }
  if (!props.email.thread_emails || props.email.thread_emails.length === 0) {
    return props.email;
  }
  return props.email.thread_emails.reduce((latest, email) => {
    const emailDate = new Date(email.date || email.timestamp || 0).getTime();
    const latestDate = new Date(latest.date || latest.timestamp || 0).getTime();
    return emailDate > latestDate ? email : latest;
  }, props.email.thread_emails[0]);
});

// Detect outbound emails to render in sandboxed iframe (prevents tracking pixel self-opens)
const isOutboundEmail = computed(() => {
  const email = props.email;
  if (!email) return false;
  const role = email.role?.toLowerCase();
  if (role === "from" || role === "sent") return true;
  if (email.folders && Array.isArray(email.folders)) {
    return email.folders.some((f) => f.toLowerCase().includes("sent"));
  }
  return false;
});

// 1x1 transparent GIF — replaces remote image sources so no network request is made.
const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBTAA7";

// Matches http://, https://, and protocol-relative (//host/...) URLs — browsers resolve the
// latter to https: just as readily, so it's an equally valid tracking-beacon vector.
const REMOTE_URL_PATTERN = /^(?:https?:)?\/\//i;
const REMOTE_URL_IN_CSS = /url\(\s*['"]?(?:https?:)?\/\/[^)'"]*['"]?\s*\)/gi;

// Outbound emails carry our own tracking pixel/links. Rendering the raw HTML (even inside a
// sandboxed iframe — `sandbox` blocks scripts/popups, NOT resource loading) still fires a real
// network request for every remote <img> or CSS background, which the tracking webhook can't
// distinguish from the recipient actually opening it. Strip remote image/background sources
// before rendering our own sent mail — covering inline style attributes, <style> blocks, and
// (via the catch fallback) malformed HTML that DOMParser can't handle.
function sanitizeOutboundHtml(html) {
  if (!html || typeof html !== "string") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("img[src]").forEach((img) => {
      const src = img.getAttribute("src") || "";
      if (REMOTE_URL_PATTERN.test(src)) {
        img.setAttribute("data-blocked-src", src);
        img.setAttribute("src", TRANSPARENT_PIXEL);
      }
    });
    doc.querySelectorAll("[style]").forEach((el) => {
      const style = el.getAttribute("style") || "";
      const sanitized = style.replace(REMOTE_URL_IN_CSS, "none");
      if (sanitized !== style) el.setAttribute("style", sanitized);
    });
    doc.querySelectorAll("style").forEach((styleTag) => {
      const css = styleTag.textContent || "";
      const sanitized = css.replace(REMOTE_URL_IN_CSS, "none");
      if (sanitized !== css) styleTag.textContent = sanitized;
    });
    return doc.body.innerHTML;
  } catch (error) {
    // DOMParser failed on malformed markup — fall back to a best-effort regex strip rather
    // than rendering the raw HTML, which would still fire the tracking request we're trying
    // to avoid.
    return html
      .replace(/<img\b[^>]*>/gi, (imgTag) =>
        /\bsrc\s*=\s*["'](?:https?:)?\/\/[^"']*["']/i.test(imgTag)
          ? imgTag.replace(/\bsrc\s*=\s*["'][^"']*["']/i, `src="${TRANSPARENT_PIXEL}"`)
          : imgTag
      )
      .replace(REMOTE_URL_IN_CSS, "none");
  }
}

const isOutboundThreadEmail = (threadEmail) => {
  if (!threadEmail) return false;
  const role = threadEmail.role?.toLowerCase();
  if (role === "from" || role === "sent") return true;
  if (threadEmail.folders && Array.isArray(threadEmail.folders)) {
    return threadEmail.folders.some((f) => f.toLowerCase().includes("sent"));
  }
  // Fallback: check if sender matches the connected account
  const senderEmail = (
    threadEmail.from_attendee?.identifier ||
    threadEmail.from?.email ||
    ""
  ).toLowerCase();
  const accountEmail = (
    props.email?.from_attendee?.identifier ||
    props.email?.from?.email ||
    ""
  ).toLowerCase();
  // If the thread email sender matches the parent email sender AND it's outbound
  if (isOutboundEmail.value && senderEmail && accountEmail && senderEmail === accountEmail)
    return true;
  return false;
};

const sortedThreadEmails = computed(() => {
  if (!props.email || !props.email.is_thread || !props.email.thread_emails) {
    return [];
  }
  return [...props.email.thread_emails].sort((a, b) => {
    const dateA = new Date(a.date || a.timestamp || 0).getTime();
    const dateB = new Date(b.date || b.timestamp || 0).getTime();
    return dateB - dateA;
  });
});

const sortedReadEvents = computed(() => {
  if (readReceipts.value && readReceipts.value.length > 0) {
    return [...readReceipts.value].sort(
      (a, b) =>
        new Date(a.readAt || a.eventTimestamp || 0).getTime() -
        new Date(b.readAt || b.eventTimestamp || 0).getTime()
    );
  }
  return emailTrackingEvents.value
    .filter((e) => {
      const t = e.eventType?.toUpperCase();
      return t === "EMAIL_OPENED" || t === "EMAIL_CLICKED";
    })
    .sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime());
});

const hasReadReceipts = computed(() => {
  return readCount.value > 0 || (sortedReadEvents.value && sortedReadEvents.value.length > 0);
});

const formattedReadReceipts = computed(() => {
  if (readReceipts.value && readReceipts.value.length > 0) {
    return sortedReadEvents.value.map((receipt, index) => ({
      ...receipt,
      readCount: index + 1,
      formattedDate: formatReadReceiptDate(receipt.readAt || receipt.eventTimestamp),
      eventType: receipt.eventType || "EMAIL_OPENED",
      recipientEmail: receipt.recipientEmail || null,
      url: receipt.url || null, // For EMAIL_CLICKED events
    }));
  }
  if (emailTrackingEvents.value && emailTrackingEvents.value.length > 0) {
    return sortedReadEvents.value.map((event, index) => ({
      ...event,
      readCount: index + 1,
      formattedDate: formatReadReceiptDate(event.eventTimestamp),
      eventType: event.eventType || "EMAIL_OPENED",
      recipientEmail: event.recipientEmail || null,
      url: event.url || null, // For EMAIL_CLICKED events
    }));
  }
  return [];
});

// Event handlers
const handleReply = () => emit("reply", props.email);
const handleForward = () => emit("forward", props.email);
const handleDelete = () => emit("delete", props.email);
const handleMarkAsRead = () => emit("mark-as-read", props.email);
const handleDownloadAttachment = (attachment) => emit("download-attachment", attachment);

const handleAiReply = () => {
  if (props.isGeneratingAI) return;
  triggerAiAction("reply", props.icps);
};

const handleAiFollowup = () => {
  if (props.isGeneratingAI) return;
  triggerAiAction("followup", props.icps);
};

// Fetch email tracking events
const fetchEmailTrackingEvents = async () => {
  const emailId = latestEmail.value?.provider_id;
  if (!emailId) return;

  isLoadingTrackingEvents.value = true;
  try {
    const response = await Contact.getEmailTrackingEvents({
      email_id: emailId,
    });
    emailTrackingEvents.value = response.data.data || [];
  } catch (error) {
    console.error("Failed to fetch tracking events:", error);
    emailTrackingEvents.value = [];
  } finally {
    isLoadingTrackingEvents.value = false;
  }
};

// Fetch email read receipts
const fetchEmailReadReceipts = async (isRetry = false) => {
  const email = latestEmail.value;
  if (!email) return;

  // Try all possible ID fields
  const emailId =
    email.provider_id || email.id || email.emailId || email.unipile_id || email.email_id;
  if (!emailId) {
    console.warn("No email ID found for read receipts", email);
    return;
  }

  // Also send the Unipile internal ID if different, so backend can match older events
  const altId =
    email.id && email.id !== emailId
      ? email.id
      : email.emailId && email.emailId !== emailId
        ? email.emailId
        : null;

  const previousCount = readCount.value;
  if (!isRetry) {
    isLoadingReadReceipts.value = true;
  }

  try {
    const response = await Unipile.getEmailReadReceipts(emailId, altId);
    if (response.data && response.data.data) {
      const receipts = (response.data.data.readReceipts || []).map((receipt) => ({
        ...receipt,
        eventType: receipt.eventType || "email_opened",
      }));

      readReceipts.value = receipts;
      readCount.value = response.data.data.readCount || receipts.length;

      if (readCount.value > previousCount && previousCount >= 0) {
        triggerReadAnimation();
      }
    }
    // Reset retry count on success
    retryCount = 0;
  } catch (error) {
    const statusCode = error?.response?.status;

    if (statusCode === 404) {
      // Email not found - handle gracefully
      console.warn("Email not found for read receipts:", emailId);
      retryCount = 0;
      // Fallback to tracking events if read receipts API fails
      await fetchEmailTrackingEvents();
    } else if (statusCode === 500 && retryCount < MAX_RETRIES) {
      // Server error - retry after delay
      retryCount++;
      console.warn(
        `Failed to fetch read receipts (attempt ${retryCount}/${MAX_RETRIES}), retrying in ${RETRY_DELAY}ms...`
      );
      setTimeout(() => {
        fetchEmailReadReceipts(true);
      }, RETRY_DELAY);
      return; // Don't set loading to false yet
    } else {
      // Other errors or max retries reached
      console.error("Failed to fetch read receipts:", error);
      retryCount = 0;
      // Fallback to tracking events if read receipts API fails
      await fetchEmailTrackingEvents();
    }
  } finally {
    if (!isRetry) {
      isLoadingReadReceipts.value = false;
    }
  }
};

const triggerReadAnimation = () => {
  isReadStatusAnimating.value = true;
  setTimeout(() => {
    isReadStatusAnimating.value = false;
  }, 600);
};

const handleRealTimeOpen = () => {
  triggerReadAnimation();

  readCount.value = (readCount.value || 0) + 1;

  fetchEmailReadReceipts();
};

// Helper function to normalize email ID for comparison
const normalizeEmailId = (id) => {
  if (!id) return null;
  return String(id).trim().toLowerCase();
};

// Helper function to extract all possible email IDs from an email object
const extractEmailIds = (email) => {
  if (!email) return [];

  const ids = [];

  // Check all possible ID fields
  const possibleFields = [
    "provider_id",
    "id",
    "emailId",
    "unipile_id",
    "email_id",
    "uid",
    "message_id",
  ];

  possibleFields.forEach((field) => {
    if (email[field]) {
      const normalized = normalizeEmailId(email[field]);
      if (normalized) ids.push(normalized);
    }
  });

  // For thread emails, also check thread_emails array
  if (email.is_thread && email.thread_emails && Array.isArray(email.thread_emails)) {
    email.thread_emails.forEach((threadEmail) => {
      possibleFields.forEach((field) => {
        if (threadEmail[field]) {
          const normalized = normalizeEmailId(threadEmail[field]);
          if (normalized && !ids.includes(normalized)) {
            ids.push(normalized);
          }
        }
      });
    });
  }

  return ids;
};

// Helper function to check if email ID matches
const isEmailMatch = (payloadEmailId, email) => {
  if (!email || !payloadEmailId) return false;

  const normalizedPayloadId = normalizeEmailId(payloadEmailId);
  if (!normalizedPayloadId) return false;

  const emailIds = extractEmailIds(email);

  return emailIds.includes(normalizedPayloadId);
};

// Setup email webhooks for real-time updates
useEmailWebhooks({
  accountId: accountId.value || undefined,
  showNotifications: false,
  onEmailOpened: (payload) => {
    const currentEmail = latestEmail.value;
    if (currentEmail && isEmailMatch(payload.emailId, currentEmail)) {
      // Immediately trigger animation and update UI
      triggerReadAnimation();

      // Update read count immediately
      const newCount = (readCount.value || 0) + 1;
      readCount.value = newCount;

      // Add to read receipts array immediately for instant UI update
      const newReceipt = {
        id: payload.eventId,
        readAt: payload.eventTimestamp,
        eventType: "EMAIL_OPENED",
        recipientEmail: payload.recipientEmail,
        contactId: payload.contactId,
        accountId: payload.accountId,
      };

      // Check if receipt already exists
      const exists = readReceipts.value.some((r) => r.id === payload.eventId);
      if (!exists) {
        readReceipts.value.push(newReceipt);
        // Sort by readAt timestamp
        readReceipts.value.sort((a, b) => {
          const dateA = new Date(a.readAt || 0).getTime();
          const dateB = new Date(b.readAt || 0).getTime();
          return dateA - dateB;
        });
      }

      // Refetch to get complete data and ensure consistency (async, non-blocking)
      fetchEmailReadReceipts().catch((err) => {
        console.error("Failed to refetch read receipts:", err);
      });
    }
  },
  onEmailLinkClicked: (payload) => {
    const currentEmail = latestEmail.value;
    if (currentEmail && isEmailMatch(payload.emailId, currentEmail)) {
      triggerReadAnimation();

      const newCount = (readCount.value || 0) + 1;
      readCount.value = newCount;

      const newReceipt = {
        id: payload.eventId,
        readAt: payload.eventTimestamp,
        eventType: "EMAIL_CLICKED",
        recipientEmail: payload.recipientEmail,
        contactId: payload.contactId,
        accountId: payload.accountId,
        url: payload.url,
        label: payload.label,
      };

      const exists = readReceipts.value.some((r) => r.id === payload.eventId);
      if (!exists) {
        readReceipts.value.push(newReceipt);
        // Sort by readAt timestamp
        readReceipts.value.sort((a, b) => {
          const dateA = new Date(a.readAt || 0).getTime();
          const dateB = new Date(b.readAt || 0).getTime();
          return dateA - dateB;
        });
      }

      fetchEmailReadReceipts().catch((err) => {
        console.error("Failed to refetch read receipts:", err);
      });
    }
  },
});

// Open read receipts popover
const openReadReceipts = async (event) => {
  if (!latestEmail.value) return;

  // Capture trigger element before async operation (currentTarget is only valid synchronously)
  const triggerElement = event.currentTarget;

  // Always re-fetch for latest data
  await fetchEmailReadReceipts();

  // Show popover if we have receipts OR if count indicates tracking data exists
  if ((sortedReadEvents.value && sortedReadEvents.value.length > 0) || readCount.value > 0) {
    readReceiptsTrigger.value = triggerElement;
    showReadReceiptsPopover.value = true;
  }
};

// Close read receipts popover
const closeReadReceipts = () => {
  showReadReceiptsPopover.value = false;
  readReceiptsTrigger.value = null;
};

// Format helpers
const formatReadReceiptDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const weekday = weekdays[date.getDay()];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  return `${weekday}, ${month} ${day}, ${year} at ${hours}:${minutesStr} ${ampm}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatThreadDate = (date) => {
  if (!date) return "Unknown date";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "Invalid date";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dayName = days[d.getDay()];
  const day = d.getDate();
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `${dayName}, ${day} ${month} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
};

// Display helpers
const getSenderDisplay = (from) => {
  if (!from) return "Unknown";

  if (from.display_name !== undefined || from.identifier) {
    const displayName = from.display_name || from.identifier;
    const identifier = from.identifier || "";
    return displayName && identifier
      ? `${displayName} <${identifier}>`
      : displayName || identifier || "Unknown";
  }

  if (Array.isArray(from)) {
    const sender = from[0];
    return sender?.name ? `${sender.name} <${sender.email}>` : sender?.email || "Unknown";
  }
  return from.name ? `${from.name} <${from.email}>` : from.email || "Unknown";
};

const getRecipientsArray = (recipients) => {
  if (!recipients) return [];
  if (Array.isArray(recipients)) return recipients;
  return [recipients];
};

const getRecipientsDisplay = (recipients) => {
  const array = getRecipientsArray(recipients);
  return array
    .map((r) => {
      if (r.display_name !== undefined || r.identifier) {
        const displayName = r.display_name || r.identifier;
        const identifier = r.identifier || "";
        return displayName && identifier
          ? `${displayName} <${identifier}>`
          : displayName || identifier || "Unknown";
      }
      return r.name ? `${r.name} <${r.email}>` : r.email || "Unknown";
    })
    .join(", ");
};

const getSenderEmail = (from) => {
  if (!from) return "";

  if (from.display_name !== undefined || from.identifier) {
    return from.identifier || "";
  }

  if (Array.isArray(from)) {
    const sender = from[0];
    return sender?.email || "";
  }
  return from.email || "";
};

const getThreadReplySender = (reply) => {
  const senderEmail = getSenderEmail(reply.from_attendee || reply.from);
  return senderEmail ? `${senderEmail} <${senderEmail}>` : "-";
};

// Reset state when email changes
const resetState = () => {
  showReadReceiptsPopover.value = false;
  readReceiptsTrigger.value = null;
  emailTrackingEvents.value = [];
  readReceipts.value = [];
  readCount.value = 0;
  isReadStatusAnimating.value = false;
};

// Watch for email changes
watch(
  () => props.email,
  (newEmail, oldEmail) => {
    if (newEmail) {
      // Check if email actually changed (by ID comparison)
      const oldEmailId = oldEmail?.provider_id || oldEmail?.id || oldEmail?.emailId;
      const newEmailId = newEmail?.provider_id || newEmail?.id || newEmail?.emailId;

      if (oldEmailId !== newEmailId) {
        resetState();

        // Seed readCount from enriched tracking data so badge shows immediately
        // For threads, use the latest email's count (not the whole thread sum)
        let seedEmail = newEmail;
        if (newEmail?.is_thread && newEmail?.thread_emails?.length > 0) {
          seedEmail = newEmail.thread_emails.reduce((latest, te) => {
            const teDate = new Date(te.date || te.timestamp || 0).getTime();
            const latestDate = new Date(latest.date || latest.timestamp || 0).getTime();
            return teDate > latestDate ? te : latest;
          }, newEmail.thread_emails[0]);
        }
        readCount.value =
          (seedEmail?.trackingOpenCount || 0) + (seedEmail?.trackingClickCount || 0);

        fetchEmailReadReceipts();
        fetchEmailTrackingEvents();
      }
    }
  },
  { immediate: true }
);

// Also watch latestEmail to handle thread email changes
watch(
  () => latestEmail.value,
  (newLatestEmail, oldLatestEmail) => {
    if (newLatestEmail) {
      const oldId = oldLatestEmail?.provider_id || oldLatestEmail?.id || oldLatestEmail?.emailId;
      const newId = newLatestEmail?.provider_id || newLatestEmail?.id || newLatestEmail?.emailId;

      if (oldId !== newId) {
        fetchEmailReadReceipts();
      }
    }
  }
);

// Polling fallback configuration
const POLL_INTERVAL = 30000; // 30 seconds
let pollIntervalId = null;

const startPolling = () => {
  if (pollIntervalId) return;

  pollIntervalId = setInterval(() => {
    if (!isSocketConnected() && latestEmail.value) {
      fetchEmailReadReceipts();
    }
  }, POLL_INTERVAL);
};

const stopPolling = () => {
  if (pollIntervalId) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
};

// Watch socket connection status
watch(
  () => {
    try {
      return isSocketConnected();
    } catch {
      return false;
    }
  },
  (isConnected) => {
    if (isConnected) {
      stopPolling();
    } else {
      startPolling();
    }
  }
);

// Fetch read receipts when component is mounted
onMounted(() => {
  if (props.email) {
    fetchEmailReadReceipts();
  }

  // Start polling if socket is not connected
  if (!isSocketConnected()) {
    startPolling();
  }
});

onUnmounted(() => {
  stopPolling();
  retryCount = 0; // Reset retry count on unmount
});

defineExpose({
  fetchEmailReadReceipts,
  fetchEmailTrackingEvents,
  handleRealTimeOpen,
});
</script>

<style scoped>
.email-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  overflow-y: auto;
}

.email-viewer__content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.email-viewer__scrollable-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 56px;
}

.email-viewer__header {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.email-viewer__header-main {
  margin-bottom: 12px;
}

.email-viewer__subject-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.email-viewer__subject {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.email-viewer__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 10px;
  color: #6b7280;
}

.email-viewer__sender,
.email-viewer__recipients {
  display: flex;
  gap: 6px;
}

.email-viewer__sender-label,
.email-viewer__recipient-label {
  font-weight: 500;
  min-width: 40px;
}

.email-viewer__sender-value,
.email-viewer__recipient-value {
  color: #111827;
}

.email-viewer__date {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 10px;
}

.email-viewer__date-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.email-viewer__date-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.email-viewer__read-status-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 32px;
  min-height: 32px;
}

.email-viewer__read-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.email-viewer__read-status:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-viewer__read-status:hover {
  background-color: #f3f4f6;
}

.email-viewer__read-status--read:hover {
  background-color: #f0fdf4;
}

.email-viewer__read-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
  color: #9ca3af;
  transition:
    color 0.3s ease,
    transform 0.3s ease;
}

.email-viewer__read-status--read .email-viewer__read-icon {
  color: #10b981 !important;
}

.email-viewer__read-status--read .email-viewer__read-icon :deep(svg),
.email-viewer__read-status--read .email-viewer__read-icon :deep(path) {
  color: #10b981 !important;
  fill: #10b981 !important;
}

.email-viewer__read-status--animating .email-viewer__read-icon {
  animation: read-pulse 0.6s ease-out;
}

@keyframes read-pulse {
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  25% {
    transform: scale(1.3);
    filter: brightness(1.2);
  }
  50% {
    transform: scale(1.1);
    filter: brightness(1.1);
  }
  75% {
    transform: scale(1.2);
    filter: brightness(1.15);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
}

.email-viewer__read-status--read {
  position: relative;
}

.email-viewer__read-status--animating::after {
  content: "";
  position: absolute;
  inset: -4px;
  border-radius: 8px;
  background: rgba(16, 185, 129, 0.2);
  animation: glow-fade 0.6s ease-out forwards;
}

.email-viewer__loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  pointer-events: none;
}

@keyframes glow-fade {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.2);
  }
}

.email-viewer__read-count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #10b981;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.email-viewer__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.email-viewer__icon-btn:hover {
  background-color: #f3f4f6;
}

.email-viewer__footer-actions {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-wrap: nowrap;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.email-viewer__footer-actions :deep(.ui-button--ghost) {
  background-color: #fff;
  border-color: #d1d5db;
  color: #111827;
  padding: 4px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
}

.email-viewer__footer-actions :deep(.ui-button--ghost:hover:not(.ui-button--disabled)) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.email-viewer__footer-actions :deep(.ui-button__icon .iconify) {
  font-size: 1.1em !important;
  width: 1.1em;
  height: 1.1em;
}

.email-viewer__footer-actions :deep(.ui-button:nth-child(3) .iconify),
.email-viewer__footer-actions :deep(.ui-button:nth-child(4) .iconify) {
  color: #f97316;
}

.email-viewer__attachments {
  padding: 10px 16px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
}

.email-viewer__attachments--compact {
  font-size: 11px;
}

.email-viewer__attachments-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 8px;
}

.email-viewer__attachments-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.email-viewer__attachment-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.email-viewer__attachment-item:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.email-viewer__attachment-name {
  flex: 1;
  font-weight: 500;
  color: #111827;
}

.email-viewer__attachment-size {
  font-size: 12px;
  color: #6b7280;
}

.email-viewer__attachment-download {
  color: #3b82f6;
  font-size: 20px;
}

.email-viewer__body {
  padding: 16px 20px;
}

.email-viewer__html-body {
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.5;
  color: #111827;
  font-size: 13px;
}

.email-viewer__html-body :deep(img) {
  max-width: 100%;
  height: auto;
}

.email-viewer__html-body-iframe {
  width: 100%;
  min-height: 200px;
  border: none;
  background: #fff;
}

.email-viewer__text-body {
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
  color: #111827;
  font-size: 13px;
}

.email-viewer__empty-body {
  padding: 48px;
  text-align: center;
  color: #6b7280;
}

.email-viewer__loading,
.email-viewer__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  padding: 3rem 2rem;
}

.email-viewer__body-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
}

.email-viewer__empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
}

.email-viewer__empty-icon {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.email-viewer__empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.email-viewer__empty-message {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

.email-viewer__gmail-thread {
  padding: 0;
  background: #fff;
}

.email-viewer__gmail-message {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.email-viewer__gmail-message:last-child {
  border-bottom: none;
}

.email-viewer__gmail-message--unread {
  background-color: #fef3c7;
}

.email-viewer__gmail-message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.email-viewer__gmail-message-sender {
  font-weight: 600;
  font-size: 13px;
  color: #111827;
}

.email-viewer__gmail-message-date {
  font-size: 12px;
  color: #6b7280;
}

.email-viewer__gmail-message-body {
  margin-top: 8px;
}

.email-viewer__gmail-body {
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #111827;
}

.email-viewer__gmail-body :deep(blockquote) {
  margin: 8px 0;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
  color: #6b7280;
  font-size: 12px;
}

.email-viewer__gmail-body :deep(p) {
  margin: 4px 0;
}

.email-viewer__gmail-body pre {
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.email-viewer__gmail-attachments {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 12px;
  color: #6b7280;
}

/* ICP Picker positioning */
.email-viewer__icp-picker-positioned {
  position: absolute;
  bottom: 57px;
  left: 16px;
  right: 16px;
  z-index: 10;
}

.email-viewer__ai-spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  border-top-color: #f97316;
  animation: ai-spin 0.7s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

@keyframes ai-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
